// Centred submit-feedback overlay. Mounts a fixed full-screen backdrop
// with a small card in the middle showing one of three states:
//
//   pending  → spinner + "Saving…"
//   success  → green tick + "Saved" (auto-dismisses after ~1.2s)
//   error    → red X + the error message (operator dismisses)
//
// Drives off a React Query mutation: pass isPending / isSuccess /
// isError / error and the component does the rest. The local phase
// machine only flips to success or error AFTER pending has been seen,
// so a freshly-mounted form (where isSuccess starts false but might
// become true from a previous run) never flashes the tick. A save that
// settles with neither flag set (a narrowed isSuccess, a masked isError)
// hides the card again rather than leaving "Saving…" over the screen.
//
// ⚠ Mount it where it SURVIVES the save: an overlay inside the form / dialog /
// view that the save closes unmounts with it, and the tick never paints.
//
// Usage:
//   <SubmitOverlay
//     isPending={m.isPending}
//     isSuccess={m.isSuccess}
//     isError={m.isError}
//     error={m.error}
//     onDismiss={() => m.reset()}
//   />

import { useEffect, useState } from 'react'
import { Check, X, Loader2 } from 'lucide-react'
import clsx from 'clsx'
import { friendlyError } from '@/lib/friendlyError'

interface SubmitOverlayProps {
  isPending:     boolean
  isSuccess:     boolean
  isError:       boolean
  error?:        unknown
  pendingLabel?: string
  successLabel?: string
  /** Watchdog copy, for an action that is not a save. The default names a
   *  save and tells the operator to check the list — wrong, and misleading,
   *  for something like an email, where the thing to check is different and
   *  a blind retry sends a second copy to a customer. */
  timeoutLabel?: string
  timeoutHint?:  string
  onDismiss?:    () => void
  /** Optional retry handler. When provided, the error card shows a primary
   *  "Try again" button alongside Dismiss — useful for transient failures
   *  (network blips, lock contention) where the operator just needs to try
   *  the same action a moment later. */
  onRetry?:      () => void
  /** Screen-specific error vocabulary, handed straight to friendlyError as
   *  its `domain` (see packhouseErrorDomains.ts). Without one, a RAISE that
   *  carries a perfectly good operator sentence but an ordinary PG code —
   *  a 23514 guard, say — is answered with friendlyError's generic
   *  "a value doesn't pass one of this record's validation rules" and the
   *  real sentence is only readable behind the Details disclosure. */
  domain?:       [RegExp, string][]
  /** For a save made from a CELL (an estimate figure, a week's bins, a
   *  per-year flag): a small badge where it is mounted — spinner, then a tick —
   *  and the error as a line in the row, with NO backdrop. The full-screen card
   *  blanked the whole grid for ≥1.2s per cell, so tabbing across 30 cells of a
   *  weekly plan was 30 blank screens (packhouse audit PM-SH-11, PW-12). Same
   *  phase machine and 60s watchdog; keep the full-screen card for forms. */
  inline?:       boolean
  /** A follow-on action offered on the success card ("Record what we paid").
   *  The tick then stays up for NEXT_STEP_MS instead of 1.2s, with the action
   *  and a "Done" button, so there is time to tap it. Taking it runs
   *  `onSelect`, then dismisses as Done does. Full-screen card only; ignored
   *  when `inline`. */
  nextStep?:     { label: string; onSelect: () => void }
}

/** The inline error line: the watchdog's own sentence when it fired with no
 *  mutation error, otherwise the operator sentence for the error. */
function inlineErrorText({ timedOut, isError, timeoutLabel, error, domain }: {
  timedOut: boolean; isError: boolean; timeoutLabel: string; error: unknown; domain?: [RegExp, string][]
}): string {
  if (timedOut && !isError) return timeoutLabel
  return friendlyError(error, domain).message
}

/** The inline rendering. The badge sits in a zero-height box, so it never moves
 *  the cell it annotates; the error is in the flow, because it must be read. */
function InlineStatus({ phase, pendingLabel, successLabel, errorText }: {
  phase: 'pending' | 'success' | 'error'
  pendingLabel: string; successLabel: string; errorText: string
}) {
  if (phase === 'error') {
    return <span role="alert" className="block text-[10px] leading-tight text-danger mt-0.5">{errorText}</span>
  }
  return (
    <span role="status" aria-live="polite" data-theme="dark" className="relative block h-0">
      <span className={clsx(
        'absolute right-0 -top-0.5 z-10 inline-flex items-center rounded-full p-0.5 border surface-sunk pointer-events-none',
        phase === 'success' ? 'border-emerald-600' : 'hairline',
      )}>
        {phase === 'pending'
          ? <Loader2 size={10} className="text-orange-400 animate-spin" aria-hidden />
          : <Check size={10} className="text-emerald-400" strokeWidth={3} aria-hidden />}
        <span className="sr-only">{phase === 'pending' ? pendingLabel : successLabel}</span>
      </span>
    </span>
  )
}

type Phase = 'hidden' | 'pending' | 'success' | 'error'

// Classify the error to surface a hint about what the operator should
// do next. Transient (network/lock) → "Try again". Stale-write/version
// → "Refresh and try again" — retry alone won't help. Anything else
// is a business-rule / validation error, no hint, dismiss-only.
// Surface the message from anything that carries one — Supabase's
// PostgrestError isn't always `instanceof Error` (varies by SDK
// version), so the previous `instanceof` check ate the real DB error
// text and the operator saw a useless "Something went wrong". Also
// handle PostgREST's `{ message, details, hint, code }` shape by
// joining the useful bits when present.
function readErrorMessage(err: unknown): string {
  if (err == null) return 'Something went wrong'
  if (err instanceof Error) return err.message || 'Something went wrong'
  if (typeof err === 'string') return err
  if (typeof err === 'object') {
    const e = err as { message?: unknown; details?: unknown; hint?: unknown; code?: unknown }
    const parts: string[] = []
    if (typeof e.message === 'string' && e.message) parts.push(e.message)
    if (typeof e.details === 'string' && e.details) parts.push(e.details)
    if (typeof e.hint    === 'string' && e.hint)    parts.push(`(hint: ${e.hint})`)
    if (parts.length > 0) {
      const text = parts.join(' · ')
      return typeof e.code === 'string' && e.code ? `[${e.code}] ${text}` : text
    }
  }
  return 'Something went wrong'
}

function classifyError(err: unknown): 'transient' | 'stale' | 'other' {
  const msg = readErrorMessage(err).toLowerCase()
  if (/stale\s*write|stale on |someone else (changed|modified)|version mismatch|concurrent|modified by/i.test(msg)) {
    return 'stale'
  }
  if (/network|failed to fetch|timed out|timeout|connection|temporarily unavailable|lock .* was released/i.test(msg)) {
    return 'transient'
  }
  return 'other'
}

// Max time the tap-blocking "Saving…" card may stay up (finding H3,
// 2026-06-11). The phase machine is driven entirely by the mutation's flags,
// so a mutation that never settles (black-holed request on an un-wrapped
// save path) used to trap the operator behind the backdrop forever — the
// literal "stuck on saving, must refresh" report. 60s sits comfortably past
// SAVE_TIMEOUT_MS (30s) and the shared-client REST timeout, so it only fires
// when every other net has already failed to settle the mutation.
const MAX_PENDING_MS = 60_000

/** How long a success card that offers a next step stays up. */
export const NEXT_STEP_MS = 4000

// One phase machine (pending → success / error, + the 60s watchdog) feeding two
// renderings, full-screen and inline; the error card is already its own
// component. Splitting the machine from its renderings would thread five pieces
// of state through every caller of the most-used save surface — same call as
// Modal's shell.
// fallow-ignore-next-line complexity
export default function SubmitOverlay({
  isPending,
  isSuccess,
  isError,
  error,
  pendingLabel = 'Saving…',
  successLabel = 'Saved',
  timeoutLabel = 'This is taking too long — the save may not have gone through.',
  timeoutHint  = 'Check the list for your entry before trying again.',
  onDismiss,
  onRetry,
  domain,
  inline = false,
  nextStep,
}: SubmitOverlayProps) {
  const hasNextStep = !!nextStep && !inline
  const [phase, setPhase] = useState<Phase>('hidden')
  const [timedOut, setTimedOut] = useState(false)

  // State machine: only flip to success/error if we passed through
  // pending — otherwise a stale isSuccess from a previous render
  // (React Query keeps the flag) would briefly show a tick on mount.
  //
  // A save can also settle with NEITHER flag: a failure its own screen answers
  // (`errorHandledElsewhere` masks isError), a refusal narrowed out of
  // isSuccess (`onlyWhenDone`, `exceptCancelled`, `isSuccess && !!data`), a
  // 'cancelled' sentinel. Nothing was saved and nothing failed that this card
  // should say, so it goes back to hidden. Left on 'pending', the backdrop ate
  // every tap for a minute and then the watchdog called it a save that "may
  // not have gone through" — MutationOverlays unmounts the card then, but a
  // card that stays mounted (a Modal `overlay`, a bare <SubmitOverlay/>) did
  // not. onDismiss is NOT called: it means "the operator has read the outcome"
  // and callers hang reset() / close-the-form / leave-the-page on it; a
  // declined confirm must leave the form, and the screen's own error, as is.
  useEffect(() => {
    if (isPending) {
      setPhase('pending')
      return
    }
    setPhase(prev => {
      if (prev !== 'pending') return prev
      if (isSuccess) return 'success'
      if (isError)   return 'error'
      return 'hidden'
    })
  }, [isPending, isSuccess, isError])

  // Watchdog: pending may not outlive MAX_PENDING_MS. Flips to the error
  // card (which accepts taps) with an honest "may or may not have saved"
  // message — the request was only abandoned client-side, the server may
  // still commit it, so the operator must check before retrying.
  //
  // The flag must SURVIVE the flip to 'error': this effect re-runs on that
  // phase change, and clearing it there (as it did) dropped every watchdog
  // card to the generic "Something went wrong", with no "may not have gone
  // through" and no check-first hint, on every screen.
  useEffect(() => {
    if (phase !== 'pending') {
      if (phase !== 'error') setTimedOut(false)
      return
    }
    const t = setTimeout(() => {
      setTimedOut(true)
      setPhase('error')
    }, MAX_PENDING_MS)
    return () => clearTimeout(t)
  }, [phase])

  // Auto-dismiss success after a short read window. Errors stay until
  // the operator dismisses (they need to read what went wrong).
  useEffect(() => {
    if (phase !== 'success') return
    const t = setTimeout(() => {
      setPhase('hidden')
      onDismiss?.()
    }, hasNextStep ? NEXT_STEP_MS : 1200)
    return () => clearTimeout(t)
  }, [phase, onDismiss, hasNextStep])

  if (phase === 'hidden') return null

  if (inline) {
    return (
      <InlineStatus phase={phase} pendingLabel={pendingLabel} successLabel={successLabel}
        errorText={inlineErrorText({ timedOut, isError, timeoutLabel, error, domain })} />
    )
  }

  const dismiss = () => {
    setPhase('hidden')
    onDismiss?.()
  }

  return (
    // ⚠️ CHECKED BOTH ERROR BRANCHES BEFORE SUPPRESSING — the first one I read
    // had no keyboard exit and this looked like a real trap. It is not: the
    // watchdog branch renders a `Dismiss` button and the ordinary one renders
    // `Try again` + `Dismiss`, so the card is escapable by keyboard. The
    // backdrop tap is a shortcut on top of that.
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      role="status"
      aria-live="polite"
      data-theme="dark"
      // pending blocks taps, success/error backdrop dismisses error on tap.
      className="fixed inset-0 z-elevated flex items-center justify-center bg-black/60 backdrop-blur-sm print:hidden motion-safe:animate-backdrop-in"
      onClick={phase === 'error' ? dismiss : undefined}
    >
      {/* Pure event plumbing: stops a tap on the CARD reaching the backdrop's
          dismiss handler. There is no interaction here to give a key binding. */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        onClick={e => e.stopPropagation()}
        className={clsx(
          'min-w-[12rem] max-w-[20rem] mx-4 rounded-2xl px-6 py-5 flex flex-col items-center gap-3 shadow-xl border',
          phase === 'pending' && 'surface-sunk hairline-strong',
          phase === 'success' && 'surface-sunk border-emerald-600',
          phase === 'error'   && 'surface-sunk border-red-700',
        )}
      >
        {phase === 'pending' && (
          <>
            <Loader2 size={36} className="text-orange-400 animate-spin" />
            <p className="text-sm text-gray-200">{pendingLabel}</p>
          </>
        )}
        {phase === 'success' && (
          <>
            <div className="w-12 h-12 rounded-full bg-emerald-500/20 border-2 border-emerald-500 flex items-center justify-center motion-safe:animate-tick-pop">
              <Check size={28} className="text-emerald-400" strokeWidth={3} />
            </div>
            {/* A label can carry an unbroken token ("Invite sent to <address>")
                and the card is at most 20rem: wrap inside it, never spill out. */}
            <p className="text-sm font-semibold text-emerald-300 text-center [overflow-wrap:anywhere]">{successLabel}</p>
            {hasNextStep && (
              <div className="flex flex-col items-stretch gap-2 w-full">
                <button type="button" className="btn-primary min-h-[44px]"
                  onClick={() => { nextStep.onSelect(); dismiss() }}>{nextStep.label}</button>
                <button type="button" className="btn-ghost min-h-[44px]" onClick={dismiss}>Done</button>
              </div>
            )}
          </>
        )}
        {phase === 'error' && (
          <ErrorCardBody timedOut={timedOut} isError={isError} error={error} domain={domain}
            timeoutLabel={timeoutLabel} timeoutHint={timeoutHint} dismiss={dismiss}
            onRetry={onRetry && (() => { setPhase('hidden'); onRetry() })} />
        )}
      </div>
    </div>
  )
}

/** The error card's body — the watchdog's honest "may not have gone through",
 *  or the operator sentence for a real failure with Try again / Dismiss.
 *  Its own component so SubmitOverlay's phase machine stays readable. */
function ErrorCardBody({ timedOut, isError, error, domain, timeoutLabel, timeoutHint, onRetry, dismiss }: {
  timedOut: boolean; isError: boolean; error: unknown; domain?: [RegExp, string][]
  timeoutLabel: string; timeoutHint: string; onRetry?: () => void; dismiss: () => void
}) {
    // Watchdog-tripped errors have no mutation error to read — surface
    // the timeout truthfully. classify() lands on 'other' (dismiss-only,
    // no Try-again) on purpose: a blind retry of a maybe-committed save
    // risks a duplicate.
    if (timedOut && !isError) {
      return (
        <>
          <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center">
            <X size={28} className="text-red-400" strokeWidth={3} />
          </div>
          <p className="text-sm font-semibold text-red-300 text-center">
            {timeoutLabel}
          </p>
          <p className="text-[11px] text-gray-400 text-center -mt-1">
            {timeoutHint}
          </p>
          <button
            type="button"
            onClick={dismiss}
            className="tap-target-y text-xs px-3 py-1.5 rounded-lg surface hover:bg-gray-700 text-gray-300"
          >
            Dismiss
          </button>
        </>
      )
    }
    const kind = classifyError(error)
    const hint = kind === 'transient' ? 'Looks transient — connection blip or lock contention. Try again.'
              : kind === 'stale'     ? 'Someone else changed this record. Refresh the page before retrying.'
                                      : null
    // Retry only makes sense for transient errors (network / lock).
    // Stale-write needs a page refresh first, not a same-state retry.
    const canRetry = !!onRetry && kind === 'transient'
    // §5.6: the headline is an operator sentence; the raw driver text
    // (when it differs) sits behind a Details disclosure, not deleted.
    const friendly = friendlyError(error, domain)
    return (
      <>
        <div className="w-12 h-12 rounded-full bg-red-500/20 border-2 border-red-500 flex items-center justify-center">
          <X size={28} className="text-red-400" strokeWidth={3} />
        </div>
        <p className="text-sm font-semibold text-red-300 text-center">
          {friendly.message}
        </p>
        {hint && (
          <p className="text-[11px] text-gray-400 text-center -mt-1">{hint}</p>
        )}
        {friendly.detail && (
          <details className="w-full">
            <summary className="text-[11px] text-muted cursor-pointer select-none text-center">Details</summary>
            <p className="text-[10px] text-gray-400 font-mono mt-1 break-all max-h-24 overflow-y-auto">{friendly.detail}</p>
          </details>
        )}
        <div className="flex items-center gap-2">
          {canRetry && (
            <button
              type="button"
              onClick={() => onRetry!()}
              className="tap-target-y text-xs px-3 py-1.5 rounded-lg bg-blue-700 hover:bg-blue-600 text-white font-semibold"
            >
              Try again
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="tap-target-y text-xs px-3 py-1.5 rounded-lg surface hover:bg-gray-700 text-gray-300"
          >
            Dismiss
          </button>
        </div>
      </>
    )
}
